/**
 * Application MUI theme.
 *
 * Consumes tokens.ts exclusively — no hardcoded values here.
 * This file must remain side-effect free so it can be imported in Node (SSR / tests).
 *
 * To extend: update tokens.ts → update themeTypes.ts → update this file.
 */
import { createTheme } from '@mui/material/styles';
import './themeTypes'; // Ensure augmentations are applied
import { colorTokens, radiiTokens, shadowTokens, spacingBase, typographyTokens } from './tokens';
export const theme = createTheme({
    // ─── Palette ────────────────────────────────────────────────────────────────
    palette: {
        primary: {
            main: colorTokens.primary,
            dark: colorTokens.primaryDark,
            light: colorTokens.primaryLight,
            contrastText: colorTokens.white,
        },
        secondary: {
            main: colorTokens.secondary,
            dark: colorTokens.secondaryDark,
            light: colorTokens.secondaryLight,
            contrastText: colorTokens.white,
        },
        error: {
            main: colorTokens.error,
            light: colorTokens.errorLight,
        },
        warning: {
            main: colorTokens.warning,
            light: colorTokens.warningLight,
        },
        success: {
            main: colorTokens.success,
            light: colorTokens.successLight,
        },
        info: {
            main: colorTokens.info,
            light: colorTokens.infoLight,
        },
        background: {
            default: colorTokens.gray50,
            paper: colorTokens.white,
        },
        text: {
            primary: colorTokens.gray800,
            secondary: colorTokens.gray600,
            disabled: colorTokens.gray400,
        },
        divider: colorTokens.gray200,
        // Custom tokens — typed via themeTypes.ts
        accent: {
            main: colorTokens.accent,
            dark: colorTokens.accentDark,
            contrastText: colorTokens.gray900,
        },
        secondaryLight: {
            main: colorTokens.secondaryLight,
            contrastText: colorTokens.secondary,
        },
    },
    // ─── Spacing ─────────────────────────────────────────────────────────────────
    // MUI default is 8px — explicit here for clarity and future forking
    spacing: spacingBase,
    // ─── Shape ───────────────────────────────────────────────────────────────────
    shape: {
        borderRadius: radiiTokens.md, // 8px
    },
    // ─── Typography ──────────────────────────────────────────────────────────────
    typography: {
        fontFamily: typographyTokens.fontFamily,
        fontWeightRegular: typographyTokens.fontWeightRegular,
        fontWeightMedium: typographyTokens.fontWeightMedium,
        fontWeightBold: typographyTokens.fontWeightBold,
        h1: {
            fontSize: typographyTokens.fontSize2xl,
            fontWeight: typographyTokens.fontWeightBold,
            lineHeight: 1.2,
            color: colorTokens.primary,
        },
        h2: {
            fontSize: typographyTokens.fontSizeXl,
            fontWeight: typographyTokens.fontWeightBold,
            lineHeight: 1.3,
        },
        h3: {
            fontSize: typographyTokens.fontSizeLg,
            fontWeight: typographyTokens.fontWeightSemiBold,
            lineHeight: 1.4,
        },
        h4: {
            fontSize: typographyTokens.fontSizeMd,
            fontWeight: typographyTokens.fontWeightSemiBold,
            lineHeight: 1.5,
        },
        h5: {
            fontSize: typographyTokens.fontSizeSm,
            fontWeight: typographyTokens.fontWeightSemiBold,
            lineHeight: 1.5,
        },
        h6: {
            fontSize: typographyTokens.fontSizeXs,
            fontWeight: typographyTokens.fontWeightSemiBold,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            lineHeight: 1.6,
        },
        body1: {
            fontSize: typographyTokens.fontSizeMd,
            fontWeight: typographyTokens.fontWeightRegular,
            lineHeight: 1.6,
        },
        body2: {
            fontSize: typographyTokens.fontSizeSm,
            fontWeight: typographyTokens.fontWeightRegular,
            lineHeight: 1.6,
        },
        caption: {
            fontSize: typographyTokens.fontSizeXs,
            fontWeight: typographyTokens.fontWeightRegular,
            lineHeight: 1.5,
        },
        overline: {
            fontSize: typographyTokens.fontSizeXs,
            fontWeight: typographyTokens.fontWeightSemiBold,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            lineHeight: 2,
        },
        // Custom variants (typed in themeTypes.ts)
        label: {
            fontSize: typographyTokens.fontSizeSm,
            fontWeight: typographyTokens.fontWeightMedium,
            lineHeight: 1.4,
        },
        caption2: {
            fontSize: 10,
            fontWeight: typographyTokens.fontWeightRegular,
            lineHeight: 1.4,
        },
    },
    // ─── Shadows ─────────────────────────────────────────────────────────────────
    shadows: [
        'none',
        shadowTokens.sm,
        shadowTokens.sm,
        shadowTokens.md,
        shadowTokens.md,
        shadowTokens.md,
        shadowTokens.lg,
        shadowTokens.lg,
        shadowTokens.lg,
        shadowTokens.lg,
        shadowTokens.lg,
        shadowTokens.lg,
        shadowTokens.lg,
        shadowTokens.lg,
        shadowTokens.lg,
        shadowTokens.lg,
        shadowTokens.lg,
        shadowTokens.lg,
        shadowTokens.lg,
        shadowTokens.lg,
        shadowTokens.lg,
        shadowTokens.lg,
        shadowTokens.lg,
        shadowTokens.lg,
        shadowTokens.lg,
    ],
    // ─── Custom tokens (accessible via theme.tokens) ──────────────────────────
    tokens: {
        radii: radiiTokens,
        shadows: shadowTokens,
    },
    // ─── Component overrides ─────────────────────────────────────────────────────
    // Global variant/override definitions live here — not scattered in sx props.
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                'html, body': {
                    margin: 0,
                    padding: 0,
                    backgroundColor: colorTokens.gray50,
                },
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    borderRadius: radiiTokens.full,
                    fontWeight: typographyTokens.fontWeightSemiBold,
                    textTransform: 'none',
                },
                sizeSmall: { padding: '4px 14px', fontSize: typographyTokens.fontSizeSm },
                sizeMedium: { padding: '8px 20px', fontSize: typographyTokens.fontSizeSm },
                sizeLarge: { padding: '10px 28px', fontSize: typographyTokens.fontSizeMd },
            },
        },
        MuiCard: {
            defaultProps: {
                elevation: 0,
            },
            styleOverrides: {
                root: {
                    borderRadius: radiiTokens.xl,
                    border: `1px solid ${colorTokens.gray200}`,
                },
            },
        },
        MuiCardHeader: {
            styleOverrides: {
                root: {
                    paddingBottom: spacingBase / 2,
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: radiiTokens.full,
                    fontWeight: typographyTokens.fontWeightSemiBold,
                },
            },
        },
        MuiTextField: {
            defaultProps: {
                size: 'small',
                variant: 'outlined',
            },
        },
        MuiFormControl: {
            defaultProps: {
                size: 'small',
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: 7,
                    backgroundColor: colorTokens.white,
                    // Border — grey, 1.5px, no colour change on hover/focus
                    // borderRadius here overrides --TextField-radius CSS var (from shape.borderRadius)
                    // so unlabelled inputs (search bars) render the same radius as labelled ones
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: colorTokens.inputBorder,
                        borderWidth: '1.5px',
                        borderRadius: 7,
                    },
                    '&:hover:not(.Mui-focused):not(.Mui-error):not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
                        borderColor: colorTokens.primary25,
                        borderWidth: '1.5px',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: colorTokens.primary,
                        borderWidth: '1.5px',
                    },
                    '&.Mui-focused': {
                        boxShadow: '0 0 0 3px rgba(0,60,113,0.10)',
                    },
                    '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                        borderColor: colorTokens.gray100,
                    },
                    '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                        borderColor: colorTokens.error,
                        borderWidth: '1.5px',
                    },
                    // Text input — 34px fixed height, 13px font
                    '&:not(.MuiInputBase-multiline) .MuiInputBase-input': {
                        padding: `0 ${spacingBase * 1.5}px`,
                        fontSize: 13,
                        fontWeight: typographyTokens.fontWeightRegular,
                        height: 34,
                        lineHeight: '34px',
                        color: colorTokens.gray800,
                        fontFamily: typographyTokens.fontFamily,
                        boxSizing: 'border-box',
                    },
                    // Override browser autofill background (Chrome/Safari inject blue-grey)
                    '& .MuiInputBase-input:-webkit-autofill': {
                        WebkitBoxShadow: `0 0 0 1000px ${colorTokens.white} inset`,
                        WebkitTextFillColor: colorTokens.gray800,
                    },
                    // Select — same 34px height
                    '&:not(.MuiInputBase-multiline) .MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: typographyTokens.fontSizeXs,
                        fontWeight: typographyTokens.fontWeightRegular,
                        padding: `0 ${spacingBase * 4}px 0 ${spacingBase * 1.5}px`,
                        height: '34px !important',
                        minHeight: 'unset !important',
                        lineHeight: '34px',
                        boxSizing: 'border-box',
                        color: colorTokens.gray800,
                        fontFamily: typographyTokens.fontFamily,
                    },
                    // Disabled state
                    '&.Mui-disabled': {
                        backgroundColor: colorTokens.gray50,
                    },
                    '&.Mui-disabled .MuiInputBase-input': {
                        color: colorTokens.gray400,
                        WebkitTextFillColor: colorTokens.gray400,
                        cursor: 'not-allowed',
                    },
                    '&.Mui-disabled .MuiSelect-select': {
                        color: colorTokens.gray400,
                        cursor: 'not-allowed',
                    },
                },
            },
        },
        MuiSelect: {
            defaultProps: {
                size: 'small',
                autoWidth: true,
                MenuProps: {
                    PaperProps: {
                        style: { minWidth: 130 },
                    },
                },
            },
            styleOverrides: {
                select: {
                    fontFamily: typographyTokens.fontFamily,
                    fontSize: typographyTokens.fontSizeXs,
                    fontWeight: typographyTokens.fontWeightRegular,
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    fontFamily: typographyTokens.fontFamily,
                    fontSize: typographyTokens.fontSizeSm,
                    fontWeight: typographyTokens.fontWeightRegular,
                    lineHeight: 1.5,
                    '&:hover': {
                        backgroundColor: colorTokens.primary10,
                    },
                    // Figma: selected item = solid primary bg + white text
                    '&.Mui-selected': {
                        backgroundColor: colorTokens.primary,
                        color: colorTokens.white,
                        fontWeight: typographyTokens.fontWeightSemiBold,
                        '&:hover': {
                            backgroundColor: colorTokens.primaryDark,
                        },
                    },
                },
            },
        },
        MuiAlert: {
            defaultProps: {
                variant: 'standard',
            },
            styleOverrides: {
                root: {
                    borderRadius: 10,
                    fontSize: typographyTokens.fontSizeSm,
                    fontFamily: typographyTokens.fontFamily,
                    alignItems: 'flex-start',
                },
                standardInfo: {
                    backgroundColor: colorTokens.infoLight,
                    color: colorTokens.secondary,
                    '& .MuiAlert-icon': { color: colorTokens.secondary },
                },
                standardWarning: {
                    backgroundColor: colorTokens.warningLight,
                    color: colorTokens.gray800,
                    '& .MuiAlert-icon': { color: colorTokens.warning },
                },
                standardError: {
                    backgroundColor: colorTokens.errorBg,
                    color: colorTokens.error,
                    '& .MuiAlert-icon': { color: colorTokens.error },
                },
                standardSuccess: {
                    backgroundColor: colorTokens.successLight,
                    color: colorTokens.gray800,
                    '& .MuiAlert-icon': { color: colorTokens.success },
                },
                message: {
                    fontSize: typographyTokens.fontSizeSm,
                    paddingTop: 1,
                },
            },
        },
        MuiCheckbox: {
            defaultProps: {
                size: 'small',
            },
            styleOverrides: {
                root: {
                    color: colorTokens.gray300,
                    borderRadius: 4,
                    '&:hover': {
                        backgroundColor: colorTokens.primary10,
                        '@media (hover: none)': { backgroundColor: 'transparent' },
                    },
                    '&.Mui-checked': {
                        color: colorTokens.primary,
                        '&:hover': {
                            backgroundColor: colorTokens.primary10,
                        },
                    },
                    '&.Mui-disabled': {
                        color: colorTokens.gray100,
                    },
                },
            },
        },
        MuiRadio: {
            defaultProps: {
                size: 'small',
            },
            styleOverrides: {
                root: {
                    color: colorTokens.gray300,
                    '&:hover': {
                        backgroundColor: colorTokens.primary10,
                        '@media (hover: none)': { backgroundColor: 'transparent' },
                    },
                    '&.Mui-checked': {
                        color: colorTokens.primary,
                        '&:hover': {
                            backgroundColor: colorTokens.primary10,
                        },
                    },
                    '&.Mui-disabled': {
                        color: colorTokens.gray100,
                    },
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    fontSize: typographyTokens.fontSizeXs,
                    fontWeight: typographyTokens.fontWeightBold,
                    color: colorTokens.primary,
                    // Vertically centered inside a 34px input
                    transform: 'translate(12px, 9px) scale(1)',
                    '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -6px) scale(0.85)',
                    },
                    '&.Mui-focused': {
                        color: colorTokens.primary,
                    },
                    '&.Mui-error': {
                        color: colorTokens.error,
                    },
                    '&.Mui-disabled': {
                        color: colorTokens.gray400,
                    },
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    fontSize: typographyTokens.fontSizeXs,
                    fontWeight: typographyTokens.fontWeightRegular,
                    marginTop: spacingBase / 2,
                    color: colorTokens.gray600,
                    '&.Mui-error': {
                        color: colorTokens.error,
                    },
                    '&.Mui-disabled': {
                        color: colorTokens.gray400,
                    },
                },
            },
        },
        MuiAccordion: {
            defaultProps: {
                disableGutters: true,
                elevation: 0,
            },
            styleOverrides: {
                root: {
                    border: 'none',
                    '&::before': { display: 'none' },
                    borderTop: `1px solid ${colorTokens.gray100}`,
                    '&:last-of-type': {
                        borderBottom: `1px solid ${colorTokens.gray100}`,
                    },
                },
            },
        },
        MuiAccordionSummary: {
            styleOverrides: {
                root: {
                    padding: `${spacingBase * 1.5}px ${spacingBase}px`,
                    '&:hover': { backgroundColor: colorTokens.gray50 },
                },
                content: {
                    alignItems: 'center',
                    gap: spacingBase * 1.5,
                },
            },
        },
        MuiAccordionDetails: {
            styleOverrides: {
                root: {
                    padding: `0 ${spacingBase * 2.5}px ${spacingBase * 2}px`,
                },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: colorTokens.gray200,
                },
            },
        },
    },
});
